const EXTENSION_TYPES: Record<string, string> = {
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    heic: 'image/heic',
    svg: 'image/svg+xml',
    pdf: 'application/pdf',
    csv: 'text/csv',
    txt: 'text/plain',
};

const TYPE_LABELS: Record<string, string> = {
    'image/png': 'PNG',
    'image/jpeg': 'JPG',
    'image/jpg': 'JPG',
    'image/gif': 'GIF',
    'image/webp': 'WEBP',
    'image/heic': 'HEIC',
    'image/svg+xml': 'SVG',
    'application/pdf': 'PDF',
    'text/csv': 'CSV',
    'text/plain': 'TXT',
};

function acceptTokens(accept: string | null | undefined): string[] {
    return (accept || '').split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
}

function extensionOf(name: string): string {
    const dot = name.lastIndexOf('.');
    return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
}

/** True when the file matches the picker's accept string (mime, wildcard or .ext); null accepts anything. */
export function acceptsFile(file: File, accept: string | null | undefined): boolean {
    const tokens = acceptTokens(accept);
    if (!tokens.length) {
        return true;
    }
    const ext = extensionOf(file.name);
    const rawType = (file.type || EXTENSION_TYPES[ext] || '').toLowerCase();
    const type = rawType === 'image/jpg' ? 'image/jpeg' : rawType;
    return tokens.some(token => {
        if (token.startsWith('.')) {
            return token.slice(1) === ext;
        }
        if (token.endsWith('/*')) {
            return type.startsWith(token.slice(0, -1));
        }
        return token === type || (token === 'image/jpg' && type === 'image/jpeg');
    });
}

/** Human list of the accepted formats, e.g. "PNG or JPG". */
export function describeAccept(accept: string | null | undefined): string {
    const labels = acceptTokens(accept).map(token => {
        if (token.startsWith('.')) {
            return token.slice(1).toUpperCase();
        }
        if (token.endsWith('/*')) {
            return token.slice(0, -2);
        }
        return TYPE_LABELS[token] || token.split('/').pop()!.toUpperCase();
    });
    const unique = labels.filter((label, i) => labels.indexOf(label) === i);
    if (unique.length <= 1) {
        return unique[0] || '';
    }
    return `${unique.slice(0, -1).join(', ')} or ${unique[unique.length - 1]}`;
}

export function formatBytes(bytes: number): string {
    if (!bytes || bytes < 1024) {
        return `${bytes || 0} B`;
    }
    const units = ['KB', 'MB', 'GB'];
    let value = bytes / 1024;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) {
        value /= 1024;
        unit++;
    }
    const rounded = value >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
    return `${rounded} ${units[unit]}`;
}

/** Null when the file passes, otherwise the message to show. */
export function validateFile(file: File, accept: string | null | undefined, maxBytes: number | null | undefined): string | null {
    if (!acceptsFile(file, accept)) {
        const formats = describeAccept(accept);
        return formats ? `${file.name} is not a supported file type. Use ${formats}.` : `${file.name} is not a supported file type.`;
    }
    if (maxBytes && file.size > maxBytes) {
        return `${file.name} is larger than ${formatBytes(maxBytes)}.`;
    }
    return null;
}
